import type { VideoMetrics } from './format-metrics-ru';
import type { AccountStatRow } from './stat-row';

export type TopVideoPlatform = 'instagram' | 'tiktok';

/**
 * Отбор роликов в отчёт «Топ видео»: по просмотрам, от большего к меньшему.
 * При равенстве выше идёт свежий — так в отчёте наверху оказывается то, что
 * ещё набирает, а не то, что набрало столько же полгода назад.
 */
export function pickTopVideos(rows: AccountStatRow[], count: number): AccountStatRow[] {
  if (count <= 0) return [];
  return [...rows]
    .sort((a, b) => {
      if (a.views !== b.views) return b.views - a.views;
      const at = new Date(a.timestamp).getTime();
      const bt = new Date(b.timestamp).getTime();
      if (at === bt) return 0;
      return at > bt ? -1 : 1;
    })
    .slice(0, count);
}

/**
 * Метрики строки статистики → метрики для подписи под роликом.
 *
 * У TikTok сохранений нет: в Display API v2 такого поля не существует вовсе
 * (в Video Object их шестнадцать, и ни одно не про сохранения; favorites_count
 * появился 21.05.2026, но в Research API — другом продукте). В AccountStatRow
 * у тиктошных роликов там стоит ноль-заглушка, и печатать её нельзя: «0
 * сохранений» читается как провал ролика, а не как отсутствие данных.
 */
export function metricsFromStat(row: AccountStatRow, platform: TopVideoPlatform): VideoMetrics {
  return {
    views: row.views,
    likes: row.likes,
    comments: row.comments,
    shares: row.shares,
    saved: platform === 'tiktok' ? null : row.saved,
  };
}

/**
 * Ссылка на ролик без хвоста: TikTok отдаёт share_url вида
 * «…/video/768…?utm_campaign=tt4d_open_api&utm_source=sbawtyq2…», а в отчётах
 * медиа ссылки чистые — «https://www.tiktok.com/@toqayev_life/video/768…».
 * Метки нужны только аналитике TikTok, ролик по чистой ссылке открывается так же.
 * На других сайтах удаляем только utm_*, сохраняя рабочие параметры и якорь.
 */
export function cleanPermalink(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (/(^|\.)(tiktok|instagram)\.com$/i.test(parsed.hostname)) {
    return `${parsed.origin}${parsed.pathname}`;
  }
  // Не пересериализуем остальные параметры: подписи и кодирование сохраняются.
  const query = parsed.search.slice(1).split('&').filter((part) => {
    const key = new URLSearchParams(part).keys().next().value;
    return !key || !/^utm_/i.test(key);
  }).join('&');
  if (query === parsed.search.slice(1)) return url;
  const hashAt = url.indexOf('#');
  const hash = hashAt < 0 ? '' : url.slice(hashAt);
  return url.slice(0, url.indexOf('?')) + (query ? `?${query}` : '') + hash;
}
